'use client';
import { useMemo } from 'react';

import type { CancelTokenSource } from 'axios';
import { parsePhoneNumberWithError } from 'libphonenumber-js';
import { create } from 'zustand';

import {
    EMAIL_LOCAL_STORAGE_KEY,
    OAUTH_PREFILL_FIRST_NAME,
    OAUTH_PREFILL_LAST_NAME,
    PHONE_LOCAL_STORAGE_KEY,
    USER_ID_LOCAL_STORAGE_KEY,
    USER_ROLE_LOCAL_STORAGE_KEY,
} from 'constants/session';
import type { FacebookPixelTrackerPayload } from 'lib/facebookPixel';
import {
    resolveAccountPolicyAvailability,
    selectPerpsOnlyStatus,
    type AccountPolicyAvailability,
} from 'lib/perpsOnlyStatus';
import { runAppLogoutTeardown } from 'lib/stores/appLogoutTeardown';
import type { ServerError, UserVo } from 'models';
import type { CreateMarketRequestRequest, LoginResponse, UpdateUserRequest } from 'models/reqres';
import type { AccessLevel } from 'models/user';
import { getCountryCodeSnapshot } from 'queries/countryCode';
import { clearNotificationFlushPending } from 'queries/notificationPreferences';
import { ensureSsnStatusLoaded } from 'queries/ssn';
import { ensureUserPropertiesLoaded, getUserPropertiesSnapshot, updateUserProperties } from 'queries/userProperties';
import { useAttributionStore } from 'stores/zustand/attributionStore';
import { usePasskeyPromptStore } from 'stores/zustand/passkeyPromptStore';
import { useReferralStore } from 'stores/zustand/referralStore';
import { displayApplicationIncomplete, useRegistrationStore } from 'stores/zustand/registrationStore';
import { handleSnackbarError, useSnackbarStore } from 'stores/zustand/snackbarStore';
import { useTwoFactorAuthStore } from 'stores/zustand/twoFactorAuthStore';
import { clearCsrfToken } from 'transport/core';
import {
    deactivateAccount as deactivateAccountTransport,
    endFESession,
    logout as logoutTransport,
    oauthLogin,
    oauthTwoFactorLogin,
    startFESession as startFESessionTransport,
    twoFactorLogin as twoFactorLoginTransport,
} from 'transport/domains/auth';
import { createMarketRequest as createMarketRequestTransport } from 'transport/domains/portfolio';
import {
    getProfile,
    requestUpdateUserEmail as requestUpdateUserEmailTransport,
    resendAccountVerification,
    updateUserEmail as updateUserEmailTransport,
    updateUserInfo,
    verifyAccount as verifyAccountTransport,
    verifyPendingEmail as verifyPendingEmailTransport,
} from 'transport/domains/profile';
import { sendResetPasswordLink, verifyPasswordReset } from 'transport/domains/signup';
import { legacyExchangeWsClient } from 'transport/wsClients';
import analyticsProvider from 'utils/analytics/analyticsProvider';
import { serverErrorSnack } from 'utils/errors';
import { clearFrontendSessionId, getValidFrontendSessionId, storeFrontendSessionId } from 'utils/frontendSession';
import { GrowthEvent, growthEventId } from 'utils/growthEventId';
import { attachReferralForNewApplicant, submitStoredReferralAfterLogin } from 'utils/loginReferral';
import { OAuthProvider } from 'utils/oauth';
import { resolveEligibleReferralForSubmission } from 'utils/referralAttribution';
import { fetchSardineSessionKey, resetSardineState } from 'utils/sardine';
import { setSprigAttribute } from 'utils/sprig';
import {
    removeCookie,
    removeLocalStorageItem,
    setCookieWithExpiration,
    setLocalStorageWithExpiration,
} from 'utils/storage';
import { uuidv4 } from 'utils/uuid';

import { isMemberAccessLevel, useAuthStore } from './authStore';
import { resetUserOrdersRefreshState, subscribeUserOrdersWs, unsubscribeUserOrdersWs } from './userOrdersStore';

const noop = (): null => null;

export enum LoginType {
    OAUTH = 'oauth',
    EMAIL = 'email',
    PASSKEY = 'passkey',
}

declare const window: any;

interface NavigatorWithUserAgentData extends Navigator {
    userAgentData?: {
        mobile?: boolean;
    };
}

function isMobileWebDevice(): boolean {
    if (typeof navigator === 'undefined') return false;

    const userAgentData = (navigator as NavigatorWithUserAgentData).userAgentData;
    if (userAgentData?.mobile) return true;

    const userAgent = navigator.userAgent || '';
    const platform = navigator.platform || '';
    const maxTouchPoints = navigator.maxTouchPoints || 0;

    return (
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|Tablet/i.test(userAgent) ||
        (platform === 'MacIntel' && maxTouchPoints > 1)
    );
}

// ---------------------------------------------------------------------------
// Reactive session flags (`loading`/`verifying`). `verified`/`first_time` were
// write-only/never-read dead fields on the old UserStore — dropped during the
// port, not carried forward.
// ---------------------------------------------------------------------------

export type UserProfileLoadState =
    | { phase: 'idle'; userId: null; error: null }
    | { phase: 'loading'; userId: UserVo['user_id']; error: null }
    | { phase: 'ready'; userId: UserVo['user_id']; error: null }
    | { phase: 'error'; userId: UserVo['user_id']; error: ServerError };

interface UserSessionState {
    loading: boolean;
    verifying: boolean;
    profileLoadState: UserProfileLoadState;
}

export const useUserSessionStore = create<UserSessionState>()(() => ({
    loading: false,
    verifying: false,
    profileLoadState: { phase: 'idle', userId: null, error: null },
}));

/**
 * Reactive account-policy lifecycle for policy-gated UI. Request-time checks
 * continue to use selectPerpsOnlyStatus directly so loading and errors fail
 * closed as `unknown`.
 */
export function useAccountPolicyAvailability(): AccountPolicyAvailability {
    const policyStatus = useAuthStore(selectPerpsOnlyStatus);
    const userId = useAuthStore((state) => state.userId);
    const profileLoadState = useUserSessionStore((state) => state.profileLoadState);

    return useMemo(
        () => resolveAccountPolicyAvailability(policyStatus, userId, profileLoadState),
        [policyStatus, profileLoadState, userId],
    );
}

type ActiveUserSessionLoad = {
    userId: string;
    promise: Promise<UserVo | null>;
};

let activeUserSessionLoad: ActiveUserSessionLoad | null = null;
let userProfileEpoch = 0;

/** Fetches the user's profile and bootstraps the session (SSN status, FE session, WS connect, properties cache). */
export function loadUserSession(onErr: (err: ServerError) => void = noop): Promise<UserVo | null> {
    const userId = useAuthStore.getState().userId ?? '';
    const profileEpoch = userProfileEpoch;
    if (!userId) {
        return Promise.resolve(null);
    }
    if (activeUserSessionLoad?.userId === userId) {
        return activeUserSessionLoad.promise;
    }

    useUserSessionStore.setState({
        loading: true,
        profileLoadState: { phase: 'loading', userId, error: null },
    });

    let resolveLoad: (user: UserVo | null) => void = noop;
    const loadPromise = new Promise<UserVo | null>((resolve) => {
        resolveLoad = resolve;
    });
    activeUserSessionLoad = { userId, promise: loadPromise };

    const finish = (user: UserVo | null): void => {
        if (activeUserSessionLoad?.promise === loadPromise) {
            useUserSessionStore.setState({ loading: false });
        }
        resolveLoad(user);
    };

    getProfile(
        userId,
        (user) => {
            // A response from a session that ended or switched users must not
            // repopulate the new session with the old account's policy.
            if (useAuthStore.getState().userId !== userId || profileEpoch !== userProfileEpoch) {
                finish(null);
                return;
            }

            useAuthStore.getState().actions.userUpdated(user);
            useUserSessionStore.setState({
                profileLoadState: { phase: 'ready', userId, error: null },
            });
            void ensureSsnStatusLoaded(userId);
            const feSessionID = getValidFrontendSessionId();
            const isLoggedInMember = useAuthStore.getState().isLoggedInMember;

            if (isLoggedInMember) {
                if (!feSessionID) {
                    startFESessionTransport(
                        userId,
                        (response) => {
                            storeFrontendSessionId(response.session_id);
                        },
                        noop,
                        (): void => {
                            legacyExchangeWsClient.connect();
                        },
                    );
                } else {
                    legacyExchangeWsClient.connect();
                }
            }

            subscribeUserOrdersWs();

            // Only clear `loading` after the properties cache is populated so the
            // color palette is set before the UI renders (important for dark mode).
            void ensureUserPropertiesLoaded().then(
                () => finish(user),
                () => finish(user),
            );
        },
        (err) => {
            if (useAuthStore.getState().userId !== userId || profileEpoch !== userProfileEpoch) {
                finish(null);
                return;
            }

            // This means that the userId we had was probably from a stale session.
            if (err.unauthorized || err.forbidden || err.notFound) {
                // We dispose of the session exactly like we do on logout.
                endSession();
                useUserSessionStore.setState({
                    profileLoadState: { phase: 'idle', userId: null, error: null },
                });
            } else {
                useUserSessionStore.setState({
                    profileLoadState: { phase: 'error', userId, error: err },
                });
            }
            onErr(err);
            finish(null);
        },
    );

    void loadPromise.then(() => {
        if (activeUserSessionLoad?.promise === loadPromise) {
            activeUserSessionLoad = null;
        }
    });

    return loadPromise;
}

/**
 * Loads a profile after any request already in flight has settled. Mutation
 * transitions use this to guarantee that account policy is read after the
 * backend has recomputed it rather than joining a pre-mutation request.
 */
export async function refreshUserSession(onErr: (err: ServerError) => void = noop): Promise<UserVo | null> {
    const inFlightLoad = activeUserSessionLoad?.promise;
    if (inFlightLoad) {
        await inFlightLoad;
    }

    return loadUserSession(onErr);
}

/** Retries an unavailable profile/policy load using the same in-flight dedupe. */
export function retryUserSession(onErr: (err: ServerError) => void = noop): Promise<UserVo | null> {
    return loadUserSession(onErr);
}

/**
 * Marks the current profile policy as stale, then guarantees a post-mutation
 * profile request. Responses started before invalidation are ignored.
 */
export function refreshUserSessionAfterPolicyChange(onErr: (err: ServerError) => void = noop): Promise<UserVo | null> {
    userProfileEpoch += 1;
    const userId = useAuthStore.getState().userId;
    useAuthStore.getState().actions.userInvalidated();
    if (userId) {
        useUserSessionStore.setState({
            loading: true,
            profileLoadState: { phase: 'loading', userId, error: null },
        });
    }
    return refreshUserSession(onErr);
}

export function handleLoginSuccess(
    // Required, and checked immediately below. This is the single funnel through
    // which a login response restores the user id and access level, so the guard
    // lives here rather than in each of the five entry points — three of which
    // (oauthLogin, GoogleOneTapScript, usePasskeySignIn) previously had none. Being
    // a required argument is the point: a new login path cannot forget it.
    attempt: LoginAttempt,
    type: LoginType,
    response: LoginResponse,
    provider?: OAuthProvider,
    onSuc: () => void = noop,
    onErr: () => void = noop,
    onTwoFactor: () => void = noop,
    onUnsupportedMobileSubUser: () => void = noop,
): void {
    // A session end landed between this login being issued and its response. Drop
    // it whole — no state restore and no callbacks, since those navigate and would
    // move a newer session's user — and undo the cookie/CSRF the response already set.
    if (!isLoginAttemptCurrent(attempt)) {
        discardStaleLoginCredentials();
        return;
    }
    // Handle the case when 2FA is required
    const authUser = useAuthStore.getState().user;
    if (authUser?.email) {
        const trackerPayload: FacebookPixelTrackerPayload = {
            page: window.location.pathname,
            event_id: uuidv4(),
        };
        // For Amplitude, this is replaced by recordSignUpMetric, but we still need it for GTM tags
        analyticsProvider.sendEvent('SignupProfileKycFailure', trackerPayload);
    }
    if (!!response.required_mfa_method && response.required_mfa_method !== 'none') {
        useTwoFactorAuthStore.getState().actions.setMfaStatus(response.required_mfa_method);
        if (response.required_mfa_method === 'authenticator-app') {
            // email login
            if (type === LoginType.EMAIL) {
                onTwoFactor();
                onErr();
            }
            // oAuth login
            else if (provider && !!response.mfa_required_token) {
                useTwoFactorAuthStore.getState().actions.setProvider(provider);
                useTwoFactorAuthStore.getState().actions.setMfaRequiredToken(response.mfa_required_token);
                onTwoFactor();
            }

            if (response.user_active === false) {
                useTwoFactorAuthStore.getState().actions.setAllowMFAReactivation(true);
            }
            return;
        }

        if (['sms', 'email', 'sms_and_email'].includes(response.required_mfa_method)) {
            // email login
            if (type === LoginType.EMAIL) {
                useTwoFactorAuthStore.getState().actions.setPhone(!!response.phone ? response.phone : '');
                useTwoFactorAuthStore.getState().actions.setMfaSentEmail(!!response.email ? response.email : '');
                onTwoFactor();
                onErr();
            }
            // oAuth login
            else if (provider && !!response.mfa_required_token) {
                useTwoFactorAuthStore.getState().actions.setProvider(provider);
                useTwoFactorAuthStore.getState().actions.setMfaRequiredToken(response.mfa_required_token);
                onTwoFactor();
            }

            if (response.user_active === false) {
                useTwoFactorAuthStore.getState().actions.setAllowReactivation(true);
            }
            return;
        }

        useSnackbarStore.getState().actions.pushMessage({
            text: 'Error logging in. User needs to configure 2FA.',
            variant: 'negative',
        });
        onErr();
        return;
    }

    setAccessLevel(response.access_level);
    setUserId(response.user_id);
    if (response.sub_user_id) {
        useAuthStore
            .getState()
            .actions.setSubUser(response.sub_user_id, response.effective_scopes ?? ['read', 'write']);
        if (isMobileWebDevice()) {
            useSnackbarStore.getState().actions.pushMessage({
                text: 'Team member accounts are not supported on mobile web.',
                variant: 'negative',
            });
            logout(noop, onUnsupportedMobileSubUser);
            return;
        }
    } else {
        useAuthStore.getState().actions.clearSubUser();
    }
    startFESession(response.user_id);
    void loadUserSession();

    // Flush attribution history on login (covers both email/password and OAuth flows)
    useRegistrationStore.getState().actions.sendAttributionHistory(response.user_id);

    if (!response.sub_user_id) {
        if (response.user_created) {
            void attachReferralForNewApplicant(response.user_id, getCountryCodeSnapshot);
        } else if (response.access_level !== 'applicant') {
            void submitStoredReferralAfterLogin(response.user_id, getCountryCodeSnapshot);
        }
    }

    // Reached only once a login actually completes — the MFA forks above return
    // early — so the passkey prompt cannot fire on a plain page load. Applicants
    // are excluded here rather than at render: an armed signal would otherwise
    // sit pending and fire the moment they became a member, mid-onboarding.
    usePasskeyPromptStore
        .getState()
        .actions.loginCompleted(
            type === LoginType.PASSKEY || !isMemberAccessLevel(response.access_level as AccessLevel),
        );

    onSuc();
}

export function handleLoginError(err: ServerError, email?: string, onExpired: () => void = noop): void {
    switch (err?.error?.code) {
        case 'user_not_kyc_verified': {
            if (email) {
                displayApplicationIncomplete(email);
            }
            break;
        }
        case 'expired_password': {
            onExpired();
            break;
        }
        case 'incorrect_mfa_code': {
            break;
        }
        case 'user_deactivated': {
            useSnackbarStore.getState().actions.pushMessage({
                ...serverErrorSnack(err),
                variant: 'negative',
            });
            useTwoFactorAuthStore.getState().actions.setAllowReactivation(true);
            break;
        }
        default: {
            const neutral = err.maintenance || err.unavailable;
            useSnackbarStore.getState().actions.pushMessage({
                ...serverErrorSnack(err),
                variant: neutral ? 'neutral' : 'negative',
            });
        }
    }
}

/**
 * Bumped by every session end — `endSession()` (explicit sign-out) and
 * `applyForcedDisconnectTeardown()` (session_expired / maintenance /
 * unavailability).
 *
 * A login request that started before one of those must not apply its response:
 * `handleLoginSuccess` restores the user id and access level into auth state,
 * localStorage and cookies, so a login resolving on the far side of a teardown
 * signs the ended user back in — over a newer session, if one has begun in the
 * tab. Cancellation can't help here: the callback transports resolve into the
 * microtask queue regardless.
 */
let loginGeneration = 0;

/** Invalidates every login request already in flight. Call from session teardown. */
function invalidateInFlightLogins(): void {
    loginGeneration += 1;
}

/**
 * The session epoch a request was issued under. Opaque on purpose: as a
 * {@link LoginAttempt} it is a required positional argument to
 * {@link handleLoginSuccess}, and an object type cannot be confused with the
 * `LoginType` / response / callbacks around it.
 */
export interface SessionEpoch {
    readonly generation: number;
}

/**
 * Snapshot the current session epoch. Capture it *before* issuing a request whose
 * continuations must not run for an ended session — before any `await`, since the
 * Sardine fetch is itself a teardown window — then check
 * {@link isSessionEpochCurrent} in every continuation.
 *
 * For anything that isn't a login this is the clock to use rather than
 * `registrationStore`'s `captureRegistrationSession`: that one also rotates when a
 * signup session ends on its own (`DuplicateAccount`, account deactivation) while the
 * user stays signed in, so an authenticated flow keyed to it would abandon work that
 * is still valid. This counter moves only on a real session end.
 */
export function captureSessionEpoch(): SessionEpoch {
    return { generation: loginGeneration };
}

/** False once a session end has happened since the epoch was captured. */
export function isSessionEpochCurrent(epoch: SessionEpoch): boolean {
    return epoch.generation === loginGeneration;
}

/** The login-request spelling of {@link SessionEpoch}; same counter, same guarantees. */
export type LoginAttempt = SessionEpoch;

/**
 * Snapshot the current epoch. Call this *before* issuing a login request — every
 * login entry point must, including the ones that live outside this module
 * (`GoogleOneTapScript`, `usePasskeySignIn`), because they call
 * `handleLoginSuccess` themselves.
 */
export function captureLoginAttempt(): LoginAttempt {
    return captureSessionEpoch();
}

/** False once a session end has happened since the attempt was captured. */
export function isLoginAttemptCurrent(attempt: LoginAttempt): boolean {
    return isSessionEpochCurrent(attempt);
}

/**
 * Released when a login is abandoned because the session ended — at any of the four
 * points where that can happen: the Sardine fetch reporting an invalidated session, the
 * pre-request generation check, and the transport's success and error callbacks.
 *
 * The login functions below suppress `onSuc`/`onErr`/`onExpired`/`onTwoFactor` on those
 * paths on purpose: those handlers navigate, toast and write session state, which is
 * exactly what must not happen for a session that is gone. But callers raise a spinner
 * *before* calling in (`setIsLogginIn(true)`, `setLoading(true)`), and clear it only from
 * those same handlers — so suppressing all of them strands the button disabled until a
 * reload. `onAborted` is the one callback that still fires, for that and only that.
 *
 * Implementations must therefore release local UI state and nothing else: no navigation,
 * no snackbar, no session or store writes. A newer user may be signed in by the time it
 * runs, and moving or messaging them is the bug this whole mechanism exists to prevent.
 *
 * This mirrors `registrationStore`'s signup actions, which call their `onFinally` on the
 * same aborts. Login has no `onFinally`, hence a dedicated callback rather than reusing
 * one of the outcome handlers.
 */
type OnLoginAborted = () => void;

/**
 * Undo the credentials a stale login response established before we could see it.
 *
 * By the time any of our callbacks run, the response has already had two effects the
 * generation guard cannot reach: the browser applied its `Set-Cookie`, re-creating the
 * session cookie the teardown had just deleted, and the transport's response
 * interceptor re-seeded the CSRF token from the response header. Suppressing only the
 * client-side restore therefore leaves the ended user authenticated to the exchange API
 * and to the proxy's session-cookie sniffing on the next reload — with the UI showing
 * signed out, which is the worst version of this bug.
 *
 * Cancelling the login transport during teardown is not an alternative: once the
 * response headers reach the browser the cookie is applied whether or not JS ever reads
 * the body, so the credentials have to be torn down again after the fact.
 *
 * If another user signed in during the window, their cookie has already been clobbered
 * by this response (same cookie name) and JS cannot read an HttpOnly value back to
 * restore it. Clearing turns "silently holding the previous user's live session" into
 * "signed out, sign in again", which is the right direction for an auth boundary.
 */
function discardStaleLoginCredentials(): void {
    if (useAuthStore.getState().isLoggedIn) {
        // A newer session is live in memory, but this response has already replaced its
        // session cookie (one cookie name per origin) and its CSRF token. Those
        // credentials are gone and unrecoverable — an HttpOnly value cannot be read back
        // — so end the session properly. Clearing persistence alone would leave the UI
        // signed in as that user with credentials the API rejects on every request,
        // until a reload or a 401 eventually forced them out.
        endSession();
        return;
    }
    useAuthStore.getState().actions.clearPersistedSession();
}

export async function twoFactorLogin(
    email: string,
    password: string,
    code: string,
    rememberDevice = false,
    reactivateUser = false,
    onSuc: () => void = noop,
    onErr: () => void = noop,
    onExpired: () => void = noop,
    onTwoFactor: () => void = noop,
    existingSardineSessionKey?: string,
    observedUserId?: string,
    onUnsupportedMobileSubUser: () => void = noop,
    onAborted: OnLoginAborted = noop,
): Promise<void> {
    const attempt = captureLoginAttempt();
    let sardineSessionKey = existingSardineSessionKey;
    if (sardineSessionKey === undefined) {
        const sardine = await fetchSardineSessionKey('login');
        // Redundant with the generation check below today — both are driven by the
        // same two teardown functions — but stated here so this stays correct if
        // either invalidation is ever triggered on its own.
        if (!sardine.ok) {
            onAborted();
            return;
        }
        sardineSessionKey = sardine.key;
    }
    if (!isLoginAttemptCurrent(attempt)) {
        onAborted();
        return;
    }
    twoFactorLoginTransport(
        {
            email,
            password,
            code,
            remember_device: rememberDevice,
            reactivate_user: reactivateUser,
            sardine_session_key: sardineSessionKey || undefined,
            observed_user_id: observedUserId || undefined,
        },
        (response) => {
            // Checked here as well as in handleLoginSuccess because the
            // updateUserProperties write below would otherwise run for the ended user.
            if (!isLoginAttemptCurrent(attempt)) {
                discardStaleLoginCredentials();
                onAborted();
                return;
            }
            if (!getUserPropertiesSnapshot().createdCredentials) {
                updateUserProperties({ createdCredentials: true }, response.user_id);
            }
            handleLoginSuccess(
                attempt,
                LoginType.EMAIL,
                response,
                undefined,
                onSuc,
                onErr,
                onTwoFactor,
                onUnsupportedMobileSubUser,
            );
        },
        (err) => {
            if (!isLoginAttemptCurrent(attempt)) {
                onAborted();
                return;
            }
            handleLoginError(err, email, onExpired);
            onErr();
        },
    );
}

export async function oAuthLogin(
    provider: OAuthProvider,
    oAuthCode: string,
    redirectUri?: string,
    onSuc: () => void = noop,
    onErr: () => void = noop,
    onTwoFactor: () => void = noop,
    onUnsupportedMobileSubUser: () => void = noop,
    onAborted: OnLoginAborted = noop,
): Promise<void> {
    const utmData = useAttributionStore.getState().utmData;
    const { referral, invalidatedReferralCode } = useReferralStore.getState();
    const referralCode = resolveEligibleReferralForSubmission(
        referral,
        useAuthStore.getState().user?.country || getCountryCodeSnapshot(),
        invalidatedReferralCode,
    );
    const attempt = captureLoginAttempt();
    const sardine = await fetchSardineSessionKey('login');
    // The session ended while this was pending; completing the OAuth exchange now
    // would sign the ended user back in over whoever is signed in by then.
    if (!sardine.ok) {
        onAborted();
        return;
    }
    // Distinct from the check above: fetchSardineSessionKey captures the Sardine reset
    // generation when it is called, so a teardown that predates the fetch leaves it
    // returning ok. The request must not go out — its response would overwrite a newer
    // user's session cookie, which handleLoginSuccess's guard would then delete.
    if (!isLoginAttemptCurrent(attempt)) {
        onAborted();
        return;
    }
    const sardineSessionKey = sardine.key;
    oauthLogin(
        provider,
        {
            oauth_code: oAuthCode,
            redirect_uri: redirectUri,
            utm_campaign: utmData.utm_campaign,
            utm_source: utmData.utm_source,
            utm_medium: utmData.utm_medium,
            utm_term: utmData.utm_term,
            utm_content: utmData.utm_content,
            utm_landing: utmData.utm_landing,
            referral_code: referralCode,
            sardine_session_key: sardineSessionKey || undefined,
        },
        (response) => {
            // handleLoginSuccess guards this case too, but it has no way to release the
            // caller's UI — so check here, where onAborted is in scope. Its
            // discardStaleLoginCredentials() still has to run: the response's Set-Cookie
            // and CSRF header have already re-authenticated the ended session.
            if (!isLoginAttemptCurrent(attempt)) {
                discardStaleLoginCredentials();
                onAborted();
                return;
            }
            handleLoginSuccess(
                attempt,
                LoginType.OAUTH,
                response,
                provider,
                () => {
                    handleOAuthLoginSuccess(response, provider);
                    onSuc();
                },
                onErr,
                onTwoFactor,
                onUnsupportedMobileSubUser,
            );
        },
        (err) => {
            // A stale login failure would toast and redirect a session that is no
            // longer the one that issued it.
            if (!isLoginAttemptCurrent(attempt)) {
                onAborted();
                return;
            }
            handleLoginError(err);
            onErr();
        },
    );
}

export async function oAuthTwoFactorLogin(
    provider: OAuthProvider,
    mfaRequiredToken: string,
    mfaCode: string,
    rememberDevice = false,
    onSuc: () => void = noop,
    onErr: () => void = noop,
    onUnsupportedMobileSubUser: () => void = noop,
    onAborted: OnLoginAborted = noop,
): Promise<void> {
    // See loginGeneration: the MFA stage is a second login request, and its success
    // callback runs handleLoginSuccess just like the initial one.
    const attempt = captureLoginAttempt();
    const sardine = await fetchSardineSessionKey('login');
    if (!sardine.ok) {
        onAborted();
        return;
    }
    if (!isLoginAttemptCurrent(attempt)) {
        onAborted();
        return;
    }
    const sardineSessionKey = sardine.key;
    oauthTwoFactorLogin(
        {
            mfa_required_token: mfaRequiredToken,
            mfa_code: mfaCode,
            remember_device: rememberDevice,
            sardine_session_key: sardineSessionKey || undefined,
        },
        (response) => {
            if (!isLoginAttemptCurrent(attempt)) {
                discardStaleLoginCredentials();
                onAborted();
                return;
            }
            handleLoginSuccess(
                attempt,
                LoginType.OAUTH,
                response,
                provider,
                () => {
                    handleOAuthLoginSuccess(response, provider);
                    onSuc();
                },
                onErr,
                noop,
                onUnsupportedMobileSubUser,
            );
        },
        (err) => {
            if (!isLoginAttemptCurrent(attempt)) {
                onAborted();
                return;
            }
            handleLoginError(err);
            onErr();
        },
    );
}

export function handleOAuthLoginSuccess(response: LoginResponse, provider: OAuthProvider): void {
    if (provider === OAuthProvider.GOOGLE) {
        if (response.given_name) {
            setLocalStorageWithExpiration(OAUTH_PREFILL_FIRST_NAME, response.given_name);
        }
        if (response.family_name) {
            setLocalStorageWithExpiration(OAUTH_PREFILL_LAST_NAME, response.family_name);
        }
    }

    // Fire only when this OAuth call actually created the account. The previous
    // !alreadySignUpWithOauth guard hydrated from the server async after this check,
    // so returning OAuth logins re-fired SignupProfile -> duplicate Registration conversions.
    if (response.user_created) {
        const trackerPayload: FacebookPixelTrackerPayload = {
            page: window.location.pathname,
            event_id: response.user_id ? growthEventId(response.user_id, GrowthEvent.Signup) : uuidv4(),
        };
        // Push the real external_id before SignupProfile so the Addressable registration
        // conversion has identity; setUserId (which also pushes it) runs after this event.
        if (typeof window !== 'undefined' && response.user_id) {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({ external_id: response.user_id, is_registered_user: true });
        }
        // For Amplitude, this is replaced by recordSignUpMetric, but we still need it for GTM tags
        analyticsProvider.sendEvent('SignupProfile', trackerPayload);
        // response.user_id override: this runs before setUserId seeds the authStore.
        updateUserProperties({ alreadySignUpWithOauth: true }, response.user_id);
    }

    setUserId(response.user_id);
    if (typeof window !== 'undefined' && window.location.hostname === 'alpha.kalshi.com') {
        setSprigAttribute('logged_in_alpha', true);
    }
}

export function logout(onSuc: () => void = noop, onFinally: () => void = noop): void {
    //The FE session must be cleared before the authentication to the backend is removed. Even a 500 to this endpoint doesn't stop from signing out.
    const feSessionID = getValidFrontendSessionId();
    !!feSessionID
        ? endFESession(
              useAuthStore.getState().userId ?? '',
              feSessionID,
              () => {
                  clearSessionID();
              },
              noop,
              () => {
                  logoutTransport(
                      () => {
                          endSession();
                          onSuc();
                      },
                      noop,
                      onFinally,
                  );
              },
          )
        : logoutTransport(
              () => {
                  endSession();
                  onSuc();
              },
              noop,
              onFinally,
          );
}

export function deactivateAccount(
    onSuc: () => void = noop,
    onError: () => void = noop,
    onFinally: () => void = noop,
): void {
    const isLoggedIn = useAuthStore.getState().isLoggedIn;
    const userId = useAuthStore.getState().userId ?? '';
    if (!isLoggedIn) {
        onFinally();
        return;
    }

    //The FE session must be cleared before the authentication to the backend is removed. Even a 500 to this endpoint doesn't stop from signing out.
    const feSessionID = getValidFrontendSessionId();
    !!feSessionID
        ? endFESession(
              useAuthStore.getState().userId ?? '',
              feSessionID,
              () => {
                  clearSessionID();
              },
              noop,
              () => {
                  deactivateAccountTransport(
                      userId,
                      () => {
                          endSession();
                          onSuc();
                      },
                      onError,
                      onFinally,
                  );
              },
          )
        : deactivateAccountTransport(
              userId,
              () => {
                  endSession();
                  onSuc();
              },
              onError,
              onFinally,
          );
}

export function endSession(): void {
    // First: a login already in flight must not be able to re-establish the
    // session this call is ending, whatever else happens below.
    invalidateInFlightLogins();
    resetSardineState();
    clearUserId();
    clearAccessLevel();
    runAppLogoutTeardown();
    resetUserOrdersRefreshState();
    clearNotificationFlushPending();
}

// Invoked by the forced-disconnect callback appEffects registers with
// lib/stores/sessionTeardownEffects.ts (session_expired / maintenance /
// unavailability) — TransportLayer sets the zustand logout state; this only
// handles the residual teardown (it does not itself touch authStore, and the
// storage cleanup lives in sessionTeardownEffects.ts).
export function applyForcedDisconnectTeardown(): void {
    // Drop any debounced notification-pref save before we tear down the
    // session — otherwise a click within the debounce window would
    // PATCH /users//subscribe with a blank userId after disconnect.
    // endSession() handles the explicit logout path; this is the
    // session_expired / maintenance / forced-disconnect path.
    clearNotificationFlushPending();
    // Mirrors endSession(). This is the path that matters most for it: signup's
    // auto-login and the MFA stage both run while the applicant is not yet fully
    // signed in, which is exactly when a forced disconnect can land between a
    // login request and its response.
    invalidateInFlightLogins();
    // Mirrors endSession(). Without it an unconsumed session key survives the
    // disconnect: startSardineFlow() returns early for the next applicant's
    // 'signup'/'kyc' flow and fetchSardineSessionKey() hands them the previous
    // applicant's key, filing their request and biometrics under that session.
    resetSardineState();
    runAppLogoutTeardown();

    resetUserOrdersRefreshState();
}

export function clearSessionID(): void {
    clearFrontendSessionId();
}

// This is the official place where we identify the logged users
export function setUserId(val: string): void {
    setLocalStorageWithExpiration(USER_ID_LOCAL_STORAGE_KEY, val);
    setCookieWithExpiration(USER_ID_LOCAL_STORAGE_KEY, val);
    useAuthStore.getState().actions.login(val);
    if (typeof window !== 'undefined') {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ external_id: val, is_registered_user: true });
    }

    // Keep Zustand authStore in sync so components reading userId from
    // the hook (e.g. GoogleOneTapScript) see the update without a reload.
    useAuthStore.getState().actions._initializeFromCookie();
}

// This is the official place where we dispose the current userId
export function clearUserId(): void {
    // Via the safe helpers, not raw localStorage. This is the first thing
    // endSession() does, so an exception here — storage access throws outright in a
    // sandboxed iframe or a browser configured to block site data — would abandon
    // the whole explicit logout: no authStore.logout() below, and so no
    // runAppLogoutTeardown() and none of the registration session teardown it runs.
    // logout() -> clearPersistedAuthStorage() erases these same three keys, so this
    // is belt-and-braces for the paths that reach clearUserId() some other way.
    removeLocalStorageItem(USER_ID_LOCAL_STORAGE_KEY);
    removeLocalStorageItem(EMAIL_LOCAL_STORAGE_KEY);
    removeLocalStorageItem(PHONE_LOCAL_STORAGE_KEY);
    removeCookie(USER_ID_LOCAL_STORAGE_KEY);
    useAuthStore.getState().actions.logout('manual');
    if (typeof window !== 'undefined') {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ is_registered_user: false });
    }
    clearCsrfToken();
    useAuthStore.getState().actions._initializeFromCookie();
}

export function clearAccessLevel(): void {
    // Safe helper for the same reason as clearUserId(): endSession() calls this
    // before runAppLogoutTeardown(), so a throw would strand the teardown.
    removeLocalStorageItem(USER_ROLE_LOCAL_STORAGE_KEY);
    useAuthStore.getState().actions.clearAccessLevel();
}

export function setAccessLevel(val: AccessLevel): void {
    setLocalStorageWithExpiration(USER_ROLE_LOCAL_STORAGE_KEY, val);
    useAuthStore.getState().actions.setAccessLevel(val);
}

export function startFESession(userId: string): void {
    startFESessionTransport(userId, (response) => {
        storeFrontendSessionId(response.session_id);
    });
}

/**
 * The profile is fetched after the session is restored from its cookie, so `user` is
 * legitimately null for a moment on boot while `userId` is already set. A caller inside a
 * blocking modal has no other channel to report that, so say it rather than no-op.
 */
function reportProfileUnavailable(onErr: () => void): void {
    useSnackbarStore.getState().actions.pushMessage({
        text: 'Your profile is still loading. Try again in a moment.',
        variant: 'negative',
    });
    onErr();
}

function updateUserInfoAndProfile(userInfo: UpdateUserRequest, onSuc: () => void, onErr: () => void = noop): void {
    const userId = useAuthStore.getState().userId;
    if (!userId) {
        reportProfileUnavailable(onErr);
        return;
    }

    const user = useAuthStore.getState().user;
    if (!user) {
        reportProfileUnavailable(onErr);
        return;
    }

    updateUserInfo(
        userId,
        userInfo,
        () => {
            // Address changes can change the backend-computed perps-only policy.
            // Wait for the replacement policy when available, but a transient
            // refresh failure must not block a still-authenticated user.
            void refreshUserSessionAfterPolicyChange(handleSnackbarError).then(() => {
                if (useAuthStore.getState().userId === userId) onSuc();
                else onErr();
            });
        },
        (err) => {
            handleSnackbarError(err);
            onErr();
        },
    );
}

/**
 * Writes whatever address `registrationStore` currently holds. The callbacks let a caller
 * that must know the write landed — the forced profile-update flow, which only advances on
 * a server-confirmed change — wait for it; the settings dialog keeps firing and forgetting.
 */
export async function updateUserAddress(onSuc: () => void = noop, onErr: () => void = noop): Promise<void> {
    const user = useAuthStore.getState().user;
    if (!user) {
        reportProfileUnavailable(onErr);
        return;
    }

    // Snapshotted before the await, not after: the Sardine fetch below can wait seconds on
    // an in-flight session, and `registrationStore` is a global that the caller's own
    // unmount cleanup may restore in the meantime — which would ship the previous address
    // as the one the user just confirmed.
    const registrationState = useRegistrationStore.getState();

    // Awaited rather than peeked: peek returns null until startSardineFlow's session
    // request lands, which would silently drop the key on a fast submit. fetch waits
    // for the in-flight session and mints one only if there isn't one.
    const sardine = await fetchSardineSessionKey('profile_change', useAuthStore.getState().userId ?? '');
    // Session ended mid-fetch: this would write the ended user's address, and its
    // 401 would be read against whoever is signed in by then.
    if (!sardine.ok) return;
    const sardineSessionKey = sardine.key;

    updateUserInfoAndProfile(
        {
            first_name: user.first_name,
            last_name: user.last_name,
            city: registrationState.city,
            state: registrationState.state,
            country: registrationState.country,
            postal_code: registrationState.postalCode,
            street1: registrationState.address1,
            street2: registrationState.address2,
            birth_date: user.birth_date,
            use_bid_ask: user.use_bid_ask,
            sardine_session_key: sardineSessionKey || undefined,
        },
        (): void => {
            useSnackbarStore.getState().actions.pushMessage({
                text: 'Successfully updated address',
                variant: 'positive',
            });
            onSuc();
        },
        onErr,
    );
}

export function requestUpdateUserEmail(
    email: string,
    onSuc: () => void = noop,
    onErr: (error: ServerError) => void = noop,
): void {
    requestUpdateUserEmailTransport(useAuthStore.getState().userId ?? '', { email: email }, onSuc, onErr);
}

export async function updateUserEmail(
    email: string,
    mfaCode: string,
    onSuc: () => void = noop,
    onErr: (error: ServerError) => void = noop,
): Promise<void> {
    const sardine = await fetchSardineSessionKey('email_change', useAuthStore.getState().userId ?? '');
    if (!sardine.ok) return;
    const sardineSessionKey = sardine.key;
    updateUserEmailTransport(
        useAuthStore.getState().userId ?? '',
        { email: email, mfa_code: mfaCode, sardine_session_key: sardineSessionKey || undefined },
        () => {
            onSuc();
            useSnackbarStore.getState().actions.pushMessage({
                text: `Confirmation email sent to ${email}`,
                variant: 'positive',
            });
        },
        onErr,
    );
}

export function verifyPendingEmail(code: string, email: string): void {
    const userId = useAuthStore.getState().userId ?? '';
    verifyPendingEmailTransport(
        userId,
        { code: code, email: email },
        () => {
            useSnackbarStore.getState().actions.pushMessage({
                text: `Email updated!`,
                variant: 'positive',
            });
        },
        handleSnackbarError,
    );
}

/** Reactive, formatted phone number — derives from authStore's user profile. */
export function usePhoneNumberWithSeparators(): string {
    const authUser = useAuthStore((s) => s.user);
    try {
        const fullNumber = `+${authUser?.country_code ?? ''}${authUser?.area_code ?? ''}${authUser?.phone_number ?? ''}`;
        const parsed = parsePhoneNumberWithError(fullNumber);
        return parsed.formatInternational();
    } catch {
        return `+${authUser?.country_code ?? ''} ${authUser?.area_code ?? ''} ${authUser?.phone_number ?? ''}`;
    }
}

export function verifyAccount(
    userId: string,
    code: string,
    onSuc: () => void,
    onErr: (err: ServerError) => void = noop,
): CancelTokenSource | null {
    const trimmedCode = code.trim();

    if (!useUserSessionStore.getState().verifying) {
        useUserSessionStore.setState({ verifying: true });
        return verifyAccountTransport(
            userId,
            { code: trimmedCode },
            () => {
                onSuc();
            },
            onErr,
            () => {
                useUserSessionStore.setState({ verifying: false });
            },
        );
    }

    return null;
}

export function resendVerification(userId: string, onSuc: () => void = noop, noPushMessage?: boolean): void {
    resendAccountVerification(
        userId,
        () => {
            if (!noPushMessage) {
                useSnackbarStore.getState().actions.pushMessage({
                    text: `We generated a new verification code for you. Check your e-mail in a minute!`,
                    variant: 'positive',
                });
            }

            onSuc();
        },
        handleSnackbarError,
    );
}

export function createMarketRequest(
    payload: CreateMarketRequestRequest,
    onSuc: () => void = noop,
    onErr: () => void = noop,
): void {
    createMarketRequestTransport(
        useAuthStore.getState().userId ?? '',
        payload,
        () => {
            onSuc();
        },
        (err) => {
            handleSnackbarError(err);
            onErr();
        },
    );
}

export async function sendPasswordReset(
    email: string,
    onSuc: () => void,
    onFinally: () => void,
    onDeactivatedUser: () => void,
): Promise<void> {
    // No user id: the backend resolves the Sardine customer from the email on
    // both the regular and the sub-user branch (sub-user resets are keyed to the
    // parent), so a client-side customerId would be redundant — and wrong for
    // sub-users. fetchSardineSessionKey never rejects — it reports an empty key when
    // a session cannot be minted, and the backend skips Sardine on an empty key — so
    // a Sardine outage cannot stop the reset mail. A block_account_update end tag
    // can: the backend rejects the reset for risk-blocked customers.
    //
    // The one caller that proceeds on `!ok` rather than aborting. The abort exists to
    // stop an *authenticated* request going out for an ended session; this request
    // carries no session at all (the backend resolves the customer from the email), so
    // there is no stale 401 to disconnect anyone with — and dropping it here would
    // silently swallow a password reset the user asked for.
    const sardine = await fetchSardineSessionKey('password_reset');
    const sardineSessionKey = sardine.ok ? sardine.key : '';

    sendResetPasswordLink(
        email,
        () => {
            onSuc();
        },
        (err) => {
            if (err.error.code === 'user_deactivated') {
                onDeactivatedUser();
            }
        },
        onFinally,
        sardineSessionKey,
    );
}

export function finishPasswordReset(
    userId: string,
    code: string,
    password: string,
    onSuc: () => void,
    onFinally: () => void,
): void {
    verifyPasswordReset(
        userId,
        code,
        password,
        () => {
            useSnackbarStore.getState().actions.pushMessage({
                text: 'Your password was reset successfully.',
                variant: 'positive',
            });
            onSuc();
        },
        (err) => {
            if (err.error.data.code === 'not_found' || err.error.data.code === 'invalid_or_expired_code') {
                useSnackbarStore.getState().actions.pushMessage({
                    text: "Your email code is expired or invalid. Please go back and click 'Forgot Your Password' again to request a new one",
                    variant: 'negative',
                });
            } else {
                handleSnackbarError(err);
            }
        },
        onFinally,
    );
}

/**
 * Resets the session's local bookkeeping (order-WS subscription, loading/
 * verifying flags) without touching authStore. Called both from the app logout
 * teardown (`resetAppStateOnLogout`) and from a not-logged-in signup-modal close
 * (`disposeWithoutExternalProducts` in the old UserStore — the two were
 * byte-for-byte identical bodies, so this single function now covers both).
 */
export function resetUserSession(): void {
    userProfileEpoch += 1;
    activeUserSessionLoad = null;
    unsubscribeUserOrdersWs();
    useUserSessionStore.setState({
        loading: false,
        verifying: false,
        profileLoadState: { phase: 'idle', userId: null, error: null },
    });
}
